import React, { useState } from "react";
import { Box, Typography, Container, Grid, Button } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import {
    MessageCircle,
    Video,
    BarChart3,
    Building2,
    ExternalLink,
    Sparkles,
    Eye,
} from "lucide-react";

// ─── Keyframe Animations ──────────────────────────────────────────────────────

const borderGlow = keyframes`
  0%, 100% { box-shadow: 0 8px 32px rgba(53, 103, 214, 0.15); }
  50%       { box-shadow: 0 20px 60px rgba(53, 103, 214, 0.35); }
`;

// ─── Shared Styled Components ─────────────────────────────────────────────────

const SectionContainer = styled(Box)(({ theme }) => ({
    paddingTop: theme.spacing(14),
    paddingBottom: theme.spacing(14),
    backgroundColor: "rgba(245, 248, 255, 0.6)",
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("md")]: {
        paddingTop: theme.spacing(10),
        paddingBottom: theme.spacing(10),
    },
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
        paddingBottom: theme.spacing(8),
    },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
    textAlign: "center",
    marginBottom: theme.spacing(8),
    [theme.breakpoints.down("md")]: { marginBottom: theme.spacing(6) },
    [theme.breakpoints.down("sm")]: { marginBottom: theme.spacing(5) },
}));

const EyebrowBadge = styled(Box)(({ theme }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 2),
    borderRadius: "100px",
    backgroundColor: "rgba(53, 103, 214, 0.08)",
    border: "1px solid rgba(53, 103, 214, 0.18)",
    marginBottom: theme.spacing(3),
}));

// ─── Card Shell — SAME FIXED HEIGHT for all 4 cards ──────────────────────────

const CardShell = styled(motion.div)(({ theme, ishighlighted }) => ({
    borderRadius: "22px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "500px",          /* ← ALL 4 cards use this exact height */
    cursor: "default",
    position: "relative",
    overflow: "hidden",
    transition: "transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), box-shadow 0.4s ease",

    ...(ishighlighted === "true"
        ? {
            background: "linear-gradient(155deg, #021f6b 0%, #022674 45%, #0d3a8a 100%)",
            boxShadow: "0 24px 64px rgba(2, 38, 116, 0.45)",
            "&:hover": {
                transform: "translateY(-10px) scale(1.02)",
                boxShadow: "0 36px 88px rgba(2, 38, 116, 0.55)",
            },
        }
        : {
            backgroundColor: "#022674",
            boxShadow: "0 8px 32px rgba(2, 38, 116, 0.25)",
            "&:hover": {
                transform: "translateY(-8px)",
                animation: `${borderGlow} 2s ease-in-out infinite`,
            },
        }),

    [theme.breakpoints.down("md")]: {
        height: "480px",      /* tablet: same for all 4 */
    },
    [theme.breakpoints.down("sm")]: {
        borderRadius: "18px",
        height: "auto",
        minHeight: "360px",
    },
}));

const CardTop = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2.5, 2.5, 0),
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(2, 2, 0),
    },
}));

const CardTitle = styled(Typography)(({ theme }) => ({
    fontSize: "1.125rem",
    fontWeight: 700,
    lineHeight: 1.25,
    [theme.breakpoints.down("md")]: { fontSize: "1rem" },
    [theme.breakpoints.down("sm")]: { fontSize: "0.9375rem" },
}));

const TitleGreen = styled("span")({ color: "#7BB58C" });
const TitleWhite = styled("span")({ color: "#FFFFFF" });

const BadgeChip = styled(Box)(({ badgecolor, badgebg }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    padding: "3px 10px",
    borderRadius: "6px",
    marginTop: "8px",
    fontSize: "0.6875rem",
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    backgroundColor: badgebg,
    color: badgecolor,
    border: `1px solid ${badgecolor}33`,
}));

/* Fixed-height image zone — image cannot push the card taller */
const ImageZone = styled(Box)(({ theme }) => ({
    height: "240px",
    flexShrink: 0,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(2, 2),
    [theme.breakpoints.down("md")]: {
        height: "195px",
    },
    [theme.breakpoints.down("sm")]: {
        height: "165px",
        padding: theme.spacing(1.5),
    },
}));

/* Content area below image — fills remaining space, no artificial gaps */
const CardBody = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0, 2.5, 2.5),
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(0, 2, 2),
    },
}));

const CardDesc = styled(Typography)(({ theme }) => ({
    fontSize: "0.8375rem",
    color: "rgba(255, 255, 255, 0.87)",
    lineHeight: 1.56,
    [theme.breakpoints.down("md")]: { fontSize: "0.8125rem" },
}));

const CTAButton = styled(Button)(({ theme }) => ({
    marginTop: "auto",       /* ← pushes CTA to bottom, no fake gap */
    padding: theme.spacing(0.75, 2),
    borderRadius: "12px",
    fontSize: "0.8125rem",
    fontWeight: 600,
    textTransform: "none",
    alignSelf: "flex-start",
    backgroundColor: "#7BB58C",
    color: "#FFFFFF",
    flexShrink: 0,
    transition: "all 0.3s ease",
    "&:hover": {
        backgroundColor: "#6aa37a",
        transform: "translateX(4px)",
    },
    [theme.breakpoints.down("sm")]: {
        fontSize: "0.75rem",
        padding: theme.spacing(0.6, 1.75),
    },
}));

const DecorCircle = styled(Box)({
    position: "absolute",
    borderRadius: "50%",
    pointerEvents: "none",
});

// ─── Animation variants ───────────────────────────────────────────────────────

const cardVariants = (delay) => ({
    initial: { opacity: 0, y: 50 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.65, delay, ease: [0.25, 0.46, 0.45, 0.94] },
});

const imgVariants = (delay) => ({
    initial: { opacity: 0, scale: 0.9 },
    whileInView: { opacity: 1, scale: 1 },
    viewport: { once: true },
    transition: { duration: 0.55, delay, ease: [0.25, 0.46, 0.45, 0.94] },
    whileHover: { y: -5, scale: 1.03 },
});

// ─── Preview Modal ────────────────────────────────────────────────────────────

const ModalOverlay = styled(motion.div)({
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(2, 18, 60, 0.75)",
    backdropFilter: "blur(10px)",
    zIndex: 1300,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
});

const ModalContent = styled(motion.div)(({ theme }) => ({
    backgroundColor: "#FFFFFF",
    borderRadius: "20px",
    padding: theme.spacing(4),
    maxWidth: "500px",
    width: "100%",
    boxShadow: "0 40px 100px rgba(0, 0, 0, 0.4)",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(3),
        borderRadius: "16px",
    },
}));

const CloseBtn = styled(Button)({
    position: "absolute",
    top: "12px",
    right: "12px",
    minWidth: "unset",
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    padding: 0,
    color: "#4C4C4C",
    backgroundColor: "rgba(0,0,0,0.06)",
    fontSize: "1rem",
    lineHeight: 1,
    "&:hover": { backgroundColor: "rgba(0,0,0,0.1)" },
});

// ─── Main Section Component ───────────────────────────────────────────────────

const HealthToolsSection = () => {
    const [previewOpen, setPreviewOpen] = useState(false);

    return (
        <SectionContainer id="health-tools">
            <Container>

                {/* ── Section Header ── */}
                <SectionHeader>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.55 }}
                    >
                        <EyebrowBadge>
                            <Sparkles style={{ width: "14px", height: "14px", color: "#3567D6" }} />
                            <Typography sx={{
                                fontSize: "0.8125rem", fontWeight: 600,
                                color: "#3567D6", letterSpacing: "0.06em", textTransform: "uppercase",
                            }}>
                                Personalised Products
                            </Typography>
                        </EyebrowBadge>

                        <Typography sx={{
                            fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
                            fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.2, mb: 2.5,
                        }}>
                            <span style={{ color: "#1D47A4" }}>Health Tools </span>
                            <span style={{ color: "#3567D6" }}>Built for Every Patient</span>
                        </Typography>

                        <Typography sx={{
                            fontSize: { xs: "0.9375rem", md: "1.0625rem" },
                            color: "#4C4C4C", lineHeight: 1.8, maxWidth: "680px", margin: "0 auto",
                        }}>
                            New age AI powered products that go beyond the report engaging patients,
                            empowering doctors, and enabling labs to deliver meaningful, continuous care.
                        </Typography>
                    </motion.div>
                </SectionHeader>

                {/* ── Cards Grid — each card written individually ── */}
                <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }} alignItems="flex-start">

                    {/* ═══════════════════════════════════════════════════════
                        CARD 1 — Follow-up Engines (WhatsApp Chatbot)
                    ═══════════════════════════════════════════════════════ */}
                    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                        <CardShell
                            ishighlighted="false"
                            {...cardVariants(0)}
                        >
                            {/* Title + Badge */}
                            <CardTop>
                                <CardTitle>
                                    <TitleGreen>Follow-up</TitleGreen>{" "}
                                    <TitleWhite>Engines</TitleWhite>
                                </CardTitle>
                                <BadgeChip
                                    badgecolor="#25D366"
                                    badgebg="rgba(37, 211, 102, 0.12)"
                                >
                                    <MessageCircle style={{ width: "10px", height: "10px" }} />
                                    Two-way
                                </BadgeChip>
                            </CardTop>

                            {/* Image */}
                            <ImageZone>
                                <motion.img
                                    src="/images/followUp.png"
                                    alt="Follow-up Engines"
                                    loading="lazy"
                                    {...imgVariants(0.12)}
                                    style={{
                                        maxHeight: "100%",
                                        maxWidth: "88%",
                                        width: "auto",
                                        objectFit: "contain",
                                    }}
                                />
                            </ImageZone>

                            {/* Description + CTA */}
                            <CardBody>
                                <CardDesc>
                                    Go beyond one-way messaging. Our Smart WhatsApp Chatbot
                                    engages patients, answers post-test queries, and lets them
                                    book check-ups — all in one conversation.
                                </CardDesc>

                                <CTAButton
                                    onClick={() => setPreviewOpen(true)}
                                    endIcon={<Eye style={{ width: "13px", height: "13px" }} />}
                                >
                                    Preview
                                </CTAButton>
                            </CardBody>
                        </CardShell>
                    </Grid>

                    {/* ═══════════════════════════════════════════════════════
                        CARD 2 — AI-Video Report
                    ═══════════════════════════════════════════════════════ */}
                    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                        <CardShell
                            ishighlighted="false"
                            {...cardVariants(0.12)}
                        >
                            {/* Title + Badge */}
                            <CardTop>
                                <CardTitle>
                                    <TitleGreen>AI-Video</TitleGreen>{" "}
                                    <TitleWhite>Report</TitleWhite>
                                </CardTitle>
                                <BadgeChip
                                    badgecolor="#7B61FF"
                                    badgebg="rgba(123, 97, 255, 0.12)"
                                >
                                    <Video style={{ width: "10px", height: "10px" }} />
                                    AI Powered
                                </BadgeChip>
                            </CardTop>

                            {/* Image — phone mockup GIF, constrained so it doesn't overflow */}
                            <ImageZone>
                                <motion.img
                                    src="/images/aiVideo.gif"
                                    alt="AI Video Report"
                                    loading="lazy"
                                    {...imgVariants(0.24)}
                                    style={{
                                        maxHeight: "100%",   /* always constrained by ImageZone height at every breakpoint */
                                        width: "auto",
                                        border: "4px solid rgba(53, 103, 214, 0.35)",
                                        borderRadius: "10px",
                                    }}
                                />
                            </ImageZone>

                            {/* Description */}
                            <CardBody>
                                <CardDesc>
                                    Personalized AI-generated video reports with individualized
                                    health tips, realistic avatars &amp; multilingual support —
                                    straight to every patient's mobile.
                                </CardDesc>
                            </CardBody>
                        </CardShell>
                    </Grid>

                    {/* ═══════════════════════════════════════════════════════
                        CARD 3 — Advanced Analytics (highlighted / dark navy)
                    ═══════════════════════════════════════════════════════ */}
                    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                        <CardShell
                            ishighlighted="true"
                            {...cardVariants(0.24)}
                        >
                            {/* Decorative background circles */}
                            <DecorCircle sx={{
                                width: 180, height: 180,
                                top: -70, right: -50,
                                background: "rgba(255,255,255,0.03)",
                            }} />
                            <DecorCircle sx={{
                                width: 110, height: 110,
                                bottom: 30, left: -40,
                                background: "rgba(255,255,255,0.025)",
                            }} />

                            {/* Title + Badge */}
                            <CardTop>
                                <CardTitle>
                                    <TitleGreen>Advanced</TitleGreen>{" "}
                                    <TitleWhite>Analytics</TitleWhite>
                                </CardTitle>
                                <BadgeChip
                                    badgecolor="#FFFFFF"
                                    badgebg="rgba(255, 255, 255, 0.15)"
                                >
                                    <BarChart3 style={{ width: "10px", height: "10px" }} />
                                    Real-time
                                </BadgeChip>
                            </CardTop>

                            {/* Image — landscape photo, contained within zone */}
                            <ImageZone>
                                <motion.img
                                    src="/images/advancedAnalytics.png"
                                    alt="Advanced Analytics"
                                    loading="lazy"
                                    {...imgVariants(0.36)}
                                    style={{
                                        maxHeight: "100%",
                                        maxWidth: "100%",
                                        width: "100%",
                                        objectFit: "cover",
                                        borderRadius: "10px",
                                    }}
                                />
                            </ImageZone>

                            {/* Description */}
                            <CardBody>
                                <CardDesc>
                                    Turn raw lab data into actionable insights. Real-time dashboards
                                    covering patient demographics, test utilization, health trends
                                    &amp; KPIs — for smarter decisions.
                                </CardDesc>
                            </CardBody>
                        </CardShell>
                    </Grid>

                    {/* ═══════════════════════════════════════════════════════
                        CARD 4 — Corporate Dashboard
                    ═══════════════════════════════════════════════════════ */}
                    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
                        <CardShell
                            ishighlighted="false"
                            {...cardVariants(0.36)}
                        >
                            {/* Title + Badge */}
                            <CardTop>
                                <CardTitle>
                                    <TitleGreen>Corporate</TitleGreen>{" "}
                                    <TitleWhite>Dashboard</TitleWhite>
                                </CardTitle>
                                <BadgeChip
                                    badgecolor="#FFB347"
                                    badgebg="rgba(255, 179, 71, 0.12)"
                                >
                                    <Building2 style={{ width: "10px", height: "10px" }} />
                                    Enterprise
                                </BadgeChip>
                            </CardTop>

                            {/* Image */}
                            <ImageZone>
                                <motion.img
                                    src="/images/corporateDashboard.png"
                                    alt="Corporate Dashboard"
                                    loading="lazy"
                                    {...imgVariants(0.48)}
                                    style={{
                                        maxHeight: "100%",
                                        maxWidth: "88%",
                                        width: "auto",
                                        objectFit: "contain",
                                    }}
                                />
                            </ImageZone>

                            {/* Description + CTA */}
                            <CardBody>
                                <CardDesc>
                                    Gamified health analytics trusted by Fortune 500 firms.
                                    Employee health checkups, intuitive dashboards &amp; fitness
                                    reports — all in one platform.
                                </CardDesc>

                                <CTAButton
                                    onClick={() => window.open("https://demo-corporate-dashboard.niroggyan.com", "_blank", "noopener,noreferrer")}
                                    endIcon={<ExternalLink style={{ width: "13px", height: "13px" }} />}
                                >
                                    Try Now
                                </CTAButton>
                            </CardBody>
                        </CardShell>
                    </Grid>

                </Grid>
            </Container>

            {/* ── WhatsApp Chatbot Preview Modal ── */}
            <AnimatePresence>
                {previewOpen && (
                    <ModalOverlay
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={() => setPreviewOpen(false)}
                    >
                        <ModalContent
                            initial={{ opacity: 0, scale: 0.88, y: 24 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.88, y: 24 }}
                            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <CloseBtn onClick={() => setPreviewOpen(false)}>✕</CloseBtn>

                            <Typography sx={{
                                fontSize: "1rem", fontWeight: 700,
                                color: "#1D47A4", mb: 2.5, pr: 4,
                            }}>
                                Follow-up Engines Preview
                            </Typography>

                            <Box
                                component="img"
                                src="https://niroggyan.s3.ap-south-1.amazonaws.com/brochure-files/whatsApp_chatbot.svg"
                                alt="WhatsApp Chatbot Preview"
                                sx={{
                                    width: "100%",
                                    maxHeight: "420px",
                                    objectFit: "contain",
                                    borderRadius: "12px",
                                    border: "1px solid rgba(53, 103, 214, 0.12)",
                                }}
                            />
                        </ModalContent>
                    </ModalOverlay>
                )}
            </AnimatePresence>
        </SectionContainer>
    );
};

export default HealthToolsSection;
